import { DSE_ICON, IconCheck, IconLock } from "../components/Icons.js";
import type { DseCategoryView, Rag, WorkstationView } from "../hooks.js";
import { useCopy } from "../copy.js";

/**
 * DSE results: a rating per category, prioritised actions, compliance framing
 * only. Every line says what guidance expects; nothing says a workstation
 * causes or prevents back pain. The third rating level renders in clay, never
 * red, and every rating carries its label in text.
 */
export default function Workstation({ view, onRetake }: { view: WorkstationView | null; onRetake: () => void }) {
  const copy = useCopy();
  if (!view) return <NoCheck onStart={onRetake} />;

  return (
    <div className="bc-screen bc-dse">
      <header className="bc-head">
        <p className="bc-kicker">Workstation check</p>
        <h1>Your setup against DSE guidance</h1>
        <p className="bc-sub">
          <span className="bc-chip bc-chip-quiet">Completed {formatDate(view.completedAt)}</span>
          <span className="bc-chip bc-chip-quiet">{locationLabel(view.workLocation)}</span>
        </p>
      </header>

      <section className="dial-hero">
        <Dial score={view.scoreOverall} rag={view.headlineRag} />
        <div className="dial-text">
          <RagBadge rag={view.headlineRag} />
          <p className="dial-line">
            {view.priority.length === 0
              ? "Your setup meets guidance across all six areas."
              : `${spell(view.priority.length)} of six areas are worth adjusting.`}
          </p>
          <p className="dial-boundary">
            This is about meeting display screen guidance and your employer's duty of care. It is not an
            assessment of your back, and it does not diagnose anything.
          </p>
        </div>
      </section>

      {view.review.state !== "none" && (
        <section className="bc-card">
          <h2>{view.review.state === "pending" ? "Being looked at" : "Advice from the review"}</h2>
          <p>{view.review.state === "pending" ? copy("dse.review.pending") : view.review.advice}</p>
          {view.review.state === "reviewed" && <p className="bc-note">{copy("dse.review.nudge")}</p>}
        </section>
      )}

      {view.priority.length > 0 && (
        <section className="bc-block">
          <div className="bc-block-head">
            <h2>Worth doing first</h2>
            <span className="bc-block-note">Most useful first</span>
          </div>
          <ol className="action-list">
            {view.priority.map((c, i) => {
              const Icon = DSE_ICON[c.key];
              return (
                <li key={c.key} className="action">
                  <span className="action-rank" aria-hidden>
                    {i + 1}
                  </span>
                  <span className={`action-icon action-icon-${c.rag}`} aria-hidden>
                    {Icon && <Icon size={18} />}
                  </span>
                  <div className="action-text">
                    <div className="action-head">
                      <span className="action-label">{c.label}</span>
                      <RagBadge rag={c.rag} />
                    </div>
                    <p className="action-do">{c.action}</p>
                    <p className="action-why">{c.guidance}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <section className="bc-block">
        <div className="bc-block-head">
          <h2>All six areas</h2>
        </div>
        <ul className="cat-list">
          {view.categories.map((c) => (
            <CategoryRow key={c.key} cat={c} />
          ))}
        </ul>
      </section>

      <section className="bc-block">
        <div className="bc-block-head">
          <h2>Check it again</h2>
        </div>
        <p className="bc-note">
          Twenty-four questions across the six areas, about three minutes. Worth redoing if your setup changes or
          you move desk.
        </p>
        <button type="button" className="bc-btn bc-btn-quiet" onClick={onRetake}>
          Retake the workstation check
        </button>
      </section>

      <section className="bc-trust">
        <IconLock size={15} />
        <p>
          Your employer sees that this check was completed, and nothing else from it.{" "}
          <b>Group counts only. Never names, never health answers.</b>
        </p>
      </section>
    </div>
  );
}

function NoCheck({ onStart }: { onStart: () => void }) {
  return (
    <div className="bc-screen bc-dse">
      <header className="bc-head">
        <p className="bc-kicker">Workstation check</p>
        <h1>Your setup against DSE guidance</h1>
      </header>
      <section className="bc-card">
        <h2>Not done yet</h2>
        <p>
          Twenty-four questions across six areas, about three minutes, all taps. It is a compliance check against
          display screen guidance, not an assessment of your back.
        </p>
        <button type="button" className="bc-btn" onClick={onStart}>
          Start the workstation check
        </button>
      </section>
    </div>
  );
}

function Dial({ score, rag }: { score: number; rag: Rag }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const filled = (Math.min(Math.max(score, 0), 100) / 100) * c;
  return (
    <div className={`dial dial-${rag}`}>
      <svg viewBox="0 0 128 128" className="dial-svg" aria-hidden>
        <circle className="dial-track" cx="64" cy="64" r={r} />
        <circle className="dial-fill" cx="64" cy="64" r={r} strokeDasharray={`${filled} ${c - filled}`} strokeDashoffset={c * 0.25} />
      </svg>
      <div className="dial-num">
        <span className="dial-value">{score}</span>
        <span className="dial-of">out of 100</span>
      </div>
    </div>
  );
}

function CategoryRow({ cat }: { cat: DseCategoryView }) {
  const Icon = DSE_ICON[cat.key];
  return (
    <li className={`cat cat-${cat.rag}`}>
      <span className={`cat-icon cat-icon-${cat.rag}`} aria-hidden>
        {cat.rag === "green" ? <IconCheck size={16} /> : Icon && <Icon size={16} />}
      </span>
      <div className="cat-text">
        <div className="cat-head">
          <span className="cat-label">{cat.label}</span>
          <span className="cat-score">{cat.score}</span>
        </div>
        <RagBadge rag={cat.rag} />
        <p className="cat-guidance">{cat.guidance}</p>
      </div>
    </li>
  );
}

export function RagBadge({ rag }: { rag: Rag }) {
  const LABEL: Record<Rag, string> = { green: "Meets guidance", amber: "Worth adjusting", red: "Needs attention" };
  return <span className={`badge badge-${rag}`}>{LABEL[rag]}</span>;
}

function locationLabel(l: WorkstationView["workLocation"]): string {
  const map: Record<WorkstationView["workLocation"], string> = {
    office: "Office setup",
    home_desk: "Home desk",
    home_improvised: "Kitchen or dining table",
    home_sofa: "Sofa or armchair",
    hybrid: "Mixed locations",
  };
  return map[l];
}

function spell(n: number): string {
  const words = ["None", "One", "Two", "Three", "Four", "Five", "Six"];
  return words[n] ?? String(n);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: "numeric", month: "long" });
}
